import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { isCurrentUserBatchOnly } from "../auth/batchOnlyAccess";
import {
  Container,
  Group,
  Button,
  Modal,
  LoadingOverlay,
  Flex,
  Pagination,
  Select,
  Paper,
  Text,
  Stack,
  Divider,
  Box,
  TextInput,
  Grid,
  Title,
  Tooltip,
  ActionIcon,
  ComboboxItem,
  Avatar,
  Badge,
  Notification,
  Menu,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { FaFileCsv, FaFilter, FaPlus, FaRedo, FaSync } from "react-icons/fa";
import {
  IconChalkboard,
  IconCheck,
  IconCloudUpload,
  IconEdit,
  IconPlus,
  IconStarFilled,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { saveAs } from "file-saver";
import { Batch, Student, Instructor } from "./types/batch.types";
import { useBatchManagement } from "./hooks/useBatchManagement";
import { useStudentManagement } from "./hooks/useStudentManagement";
import { BatchForm } from "./components/BatchForm";
import { StudentForm } from "./components/StudentForm";
import { InstructorForm } from "./components/InstructorForm";
import { BatchTable } from "./components/BatchTable";
import { BatchFilters } from "./components/BatchFilters";
import { getAllInstructorDetails } from "../api/dynamodb-db-api";
import CreateStudent from "./components/CreateStudent";
import AddStudentInBatch from "./components/AddStudentInBatch";

import UploadResourceModal from "./components/UploadResourceModal";
import ViewResourcesModal from "./components/ViewResourcesModal";
// import SweetnessRatingFilterModal from "./components/SweetnessRatingFilterModal"; // COMMENTED OUT

import { ApiService } from "../api/api-service";
import { showNotification } from "@mantine/notifications";

const PAGE_OPTIONS = [15, 30, 50, 100];

// Yes/No filter: "" = no filter, "Not Set" = batch has no value saved yet
const matchesYesNoFilter = (value: string | undefined, filter: string) => {
  if (!filter) return true;
  if (filter === "Not Set") return !value;
  return value === filter;
};

const Batches: React.FC = () => {
  // Media Query for Mobile Detection
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();

  // SuperAdmin detection
  const isSuperAdmin = useMemo(() => {
    try {
      const roles = JSON.parse(localStorage.getItem("applicationRole") || "[]");
      return roles.some(
        (r: any) => r.appName === "adminpanel" && r.appRole === "superAdmin",
      );
    } catch {
      return false;
    }
  }, []);

  // Batch-only accounts get full Batches access (incl. Attended Hours,
  // gated by isSuperAdmin above) but never the Journey Dashboard / Recent
  // Changes shortcuts, even though they carry the superAdmin app role.
  const batchOnly = useMemo(() => isCurrentUserBatchOnly(), []);

  // Batch Management State
  const {
    loading,
    setLoading,
    batches,
    fetchBatches,
    createBatch,
    updateBatch,
    deleteBatch,
    updateBatchStatus,
    linkBatches,
    unlinkBatches,
    admins,
    adminDropdown,
    fetchAdminsData,
  } = useBatchManagement();

  // Student Management State
  const {
    loading: studentLoading,
    setLoading: setStudentLoading,
    studentsDropdown,
    setStudentsDropdown,
    studentsRef,
    fetchStudents,
    createStudent,
    fetchNextStudentId,
    validateStudentEmail,
  } = useStudentManagement();

  // UI State
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // LMS creation confirmation — asked right before a new batch is saved
  const [lmsConfirmOpen, setLmsConfirmOpen] = useState(false);
  const [pendingBatchData, setPendingBatchData] = useState<Partial<Batch> | null>(
    null,
  );
  const [creatingBatch, setCreatingBatch] = useState(false);

  // "Update in LMS" — manual re-trigger from the Batch Details modal
  const [updatingLms, setUpdatingLms] = useState(false);

  // Selected Batch State
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);

  // Filter State — initialized from sessionStorage for persistence across navigation
  const savedFilters = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("batchFilters");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [combinedFilter, setCombinedFilter] = useState(
    savedFilters?.combinedFilter || "",
  );
  const [trainerNameFilter, setTrainerNameFilter] = useState(
    savedFilters?.trainerNameFilter || "",
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    savedFilters?.statusFilter || "",
  );
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState<string[]>(
    savedFilters?.scheduleStatusFilter || [],
  );


  const [filterAdmin, setFilterAdmin] = useState<string>(
    savedFilters?.filterAdmin || "",
  );
  const [studentNameFilter, setStudentNameFilter] = useState(
    savedFilters?.studentNameFilter || "",
  );
  // "Yes" | "No" | "Not Set" | "" (no filter)
  const [orientationFilter, setOrientationFilter] = useState<string>(
    savedFilters?.orientationFilter || "",
  );
  const [bridgeCourseFilter, setBridgeCourseFilter] = useState<string>(
    savedFilters?.bridgeCourseFilter || "",
  );

  // Auto-open filters panel when returning with saved filters
  const [showFilters, setShowFilters] = useState(!!savedFilters);
  // const [sweetnessFilterOpen, setSweetnessFilterOpen] = useState(false); // COMMENTED OUT

  // Pagination State — initialized from sessionStorage for persistence
  const [currentPage, setCurrentPage] = useState(
    savedFilters?.currentPage || 1,
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    savedFilters?.rowsPerPage || 15,
  );

  // Persist filters to sessionStorage on every change
  useEffect(() => {
    const hasAnyFilter =
      combinedFilter ||
      trainerNameFilter ||
      statusFilter ||
      scheduleStatusFilter.length > 0 ||
      filterAdmin ||
      studentNameFilter ||
      orientationFilter ||
      bridgeCourseFilter ||
      currentPage > 1 ||
      rowsPerPage !== 15;
    if (hasAnyFilter) {
      sessionStorage.setItem(
        "batchFilters",
        JSON.stringify({
          combinedFilter,
          trainerNameFilter,
          statusFilter,
          scheduleStatusFilter,
          filterAdmin,
          studentNameFilter,
          orientationFilter,
          bridgeCourseFilter,
          currentPage,
          rowsPerPage,
        }),
      );
    } else {
      sessionStorage.removeItem("batchFilters");
    }
  }, [
    combinedFilter,
    trainerNameFilter,
    statusFilter,
    scheduleStatusFilter,
    filterAdmin,
    studentNameFilter,
    orientationFilter,
    bridgeCourseFilter,
    currentPage,
    rowsPerPage,
  ]);

  // Session Editing State
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [sessionInputValue, setSessionInputValue] = useState<number>(0);

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedBatchForResource, setSelectedBatchForResource] = useState<
    string | null
  >(null);

  // Form Data State
  const [formData, setFormData] = useState<Partial<Batch>>({
    batch_no: "",
    courseName: "",
    trainerName: "",
    admin: "",
    MouStatus: "",
    batchStatus: "On-going",
    students: [],
    instructor: [],
  });

  // Consolidated instructor state
  const [instructors, setInstructors] = useState<ComboboxItem[]>([]);
  const instructorsRef = useRef<Instructor[]>([]);

  const [studentData, setStudentData] = useState({
    student_id: "",
    name: "",
    email: "",
    mobile: "",
    enrollDate: "",
  });

  // Fetch instructor details from backend
  const fetchInstructors = useCallback(async () => {
    try {
      const response = await getAllInstructorDetails("instructors");

      instructorsRef.current = response;
      const instructorOptions: ComboboxItem[] = response.map(
        (instructor: any) => ({
          label: instructor.email,
          value: instructor.email,
        }),
      );
      setInstructors(instructorOptions);
    } catch (error) {
      console.error("Error fetching instructors:", error);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
    fetchStudents();
    fetchAdminsData();
    fetchInstructors();
  }, [fetchBatches, fetchStudents, fetchInstructors, fetchAdminsData]);

  // Filtered Data
  const filteredBatch = useMemo(() => {
    return batches.filter((batch) => {
      // 1) Combined search (combinedFilter) — split by commas and match any term against batch_no OR courseName
      if (combinedFilter) {
        const terms = combinedFilter
          .split(",")
          .map((t: string) => t.trim().toLowerCase())
          .filter(Boolean);
        if (terms.length > 0) {
          const matchesAny = terms.some((term: string) => {
            const matchesBatchNo = (
              batch.batch_no?.toLowerCase() || ""
            ).includes(term);
            const matchesCourseName = (
              batch.courseName?.toLowerCase() || ""
            ).includes(term);
            return matchesBatchNo || matchesCourseName;
          });
          if (!matchesAny) return false;
        }
      }

      // 2) Specific field filters — all of these must pass if they are set
      // (course name handling is now covered by combinedFilter)
      if (trainerNameFilter) {
        if (
          !(batch.trainerName || "")
            .toLowerCase()
            .includes(trainerNameFilter.toLowerCase())
        )
          return false;
      }

      if (statusFilter) {
        // Match status case-insensitively (allow partial match)
        if (
          !(batch.batchStatus || "")
            .toLowerCase()
            .includes(statusFilter.toLowerCase())
        )
          return false;
      }

      if (scheduleStatusFilter && scheduleStatusFilter.length > 0) {
        const batchSchedule = (batch.scheduleStatus || "").toLowerCase();
        const selected = scheduleStatusFilter.map((s) => s.toLowerCase());
        // If batch schedule does not match any selected schedule statuses, exclude it
        if (!selected.some((s) => batchSchedule.includes(s))) return false;
      }

      if (filterAdmin) {
        if (
          !(batch.admin || "").toLowerCase().includes(filterAdmin.toLowerCase())
        )
          return false;
      }

      if (studentNameFilter) {
        const matches = batch.students?.some((student) =>
          (student.name || "")
            .toLowerCase()
            .includes(studentNameFilter.toLowerCase()),
        );
        if (!matches) return false;
      }

      if (!matchesYesNoFilter(batch.orientation, orientationFilter)) return false;
      if (!matchesYesNoFilter(batch.bridgeCourse, bridgeCourseFilter)) return false;

      // If all active filters passed, include the batch
      return true;
    });
  }, [
    batches,
    combinedFilter,
    trainerNameFilter,
    statusFilter,
    scheduleStatusFilter,
    filterAdmin,
    studentNameFilter,
    orientationFilter,
    bridgeCourseFilter,
  ]);

  const paginatedBatch = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredBatch.slice(start, start + rowsPerPage);
  }, [filteredBatch, currentPage, rowsPerPage]);

  // Handlers
  const handleBatchClick = (batch: Batch) => {
    setSelectedBatch(batch);
    setBatchModalOpen(true);
    setEditMode(false);
  };

  // Unified instructor change handler
  const handleInstructorChange = (
    index: number,
    field: keyof Instructor,
    value: any,
    isEdit: boolean = false,
  ) => {
    if (isEdit && selectedBatch) {
      setSelectedBatch((prev) => {
        if (!prev) return prev;

        const updatedInstructors = [...(prev.instructor || [])];
        // Ensure the index exists
        if (!updatedInstructors[index]) {
          updatedInstructors[index] = {
            name: "",
            email: "",
            dateOfAssign: new Date().toISOString().split("T")[0],
            mouHours: "",
            mouAmount: undefined,
            trainerLeaves: undefined,
            attendedHours: "",
            batchTiming: "",
          };
        }

        updatedInstructors[index] = {
          ...updatedInstructors[index],
          [field]: value,
        };

        return { ...prev, instructor: updatedInstructors };
      });
    } else {
      setFormData((prev) => {
        const updatedInstructors = [...(prev.instructor || [])];

        // Ensure the index exists
        if (!updatedInstructors[index]) {
          updatedInstructors[index] = {
            name: "",
            email: "",
            dateOfAssign: new Date().toISOString().split("T")[0],
            mouHours: "",
            mouAmount: undefined,
            trainerLeaves: undefined,
            attendedHours: "",
            batchTiming: "",
          };
        }

        updatedInstructors[index] = {
          ...updatedInstructors[index],
          [field]: value,
        };

        return { ...prev, instructor: updatedInstructors };
      });
    }
  };

  // Handle instructor email selection with auto-populate name
  const handleInstructorEmailChange = (
    index: number,
    email: string | null,
    isEdit: boolean = false,
  ) => {
    if (!email) return;

    // Find the selected instructor from the ref
    const selectedInstructor = instructorsRef.current.find(
      (instructor) => instructor.email === email,
    );

    // Update email field
    handleInstructorChange(index, "email", email, isEdit);

    // Update name field if instructor found
    if (selectedInstructor) {
      handleInstructorChange(index, "name", selectedInstructor.name, isEdit);
    }
  };

  const handleCreateBatch = async () => {
    if (!formData.batch_no?.trim() || !formData.trainerName?.trim()) {
      alert("Please fill all required fields!");
      return;
    }

    // if (formData.students?.length === 0 || formData.students?.some(student => !student.name?.trim())) {
    //   alert("At least one student with a valid name must be added!");
    //   return;
    // }

    if (
      formData.instructor?.length === 0 ||
      formData.instructor?.some(
        (instructor) => !instructor.email?.trim() || !instructor.name?.trim(),
      )
    ) {
      alert("At least one instructor with valid email and name must be added!");
      return;
    }

    // Format instructor data before saving
    const formattedInstructors = formData.instructor?.map((instructor) => ({
      name: instructor.name || "",
      email: instructor.email || "",
      mouAmount: Number(instructor.mouAmount) || 0,
      trainerLeaves: Number(instructor.trainerLeaves) || 0,
      dateOfAssign:
        instructor.dateOfAssign || new Date().toISOString().split("T")[0],
      mouHours: instructor.mouHours || "0",
      attendedHours: instructor.attendedHours || "0",
      batchTiming: instructor.batchTiming || "0",
    }));

    const batchData = {
      ...formData,
      instructor: formattedInstructors,
    };

    // Ask whether this batch should also be created on the LMS (triggers
    // the recording-automation Lambda) before actually saving it.
    setPendingBatchData(batchData);
    setLmsConfirmOpen(true);
  };

  const handleConfirmCreateBatch = async (triggerLms: boolean) => {
    if (!pendingBatchData) return;
    try {
      setCreatingBatch(true);
      const success = await createBatch({ ...pendingBatchData, triggerLms });
      if (success) {
        setLmsConfirmOpen(false);
        setPendingBatchData(null);
        setCreateModalOpen(false);
        resetFormData();
        fetchBatches();
      }
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleUpdateBatch = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);

      // Check if this is a new batch linking operation
      const originalBatch = batches.find(
        (b) => b.batch_no === selectedBatch.batch_no,
      );
      const isNewLinking =
        originalBatch?.linkedBatches?.length === 0 &&
        selectedBatch.linkedBatches &&
        selectedBatch.linkedBatches.length > 0;

      // Format instructor data before updating
      const formattedInstructors = selectedBatch.instructor?.map(
        (instructor) => ({
          name: instructor.name || "",
          email: instructor.email || "",
          mouAmount: Number(instructor.mouAmount) || 0,
          trainerLeaves: Number(instructor.trainerLeaves) || 0,
          dateOfAssign:
            instructor.dateOfAssign || new Date().toISOString().split("T")[0],
          mouHours: instructor.mouHours || "0",
          attendedHours: instructor.attendedHours || "0",
          batchTiming: instructor.batchTiming || "0",
        }),
      );

      const updatedBatch = {
        ...selectedBatch,
        instructor: formattedInstructors,
      };

      const success = await updateBatch(selectedBatch.batch_no, updatedBatch);

      if (success) {
        // Show appropriate notification based on operation type
        if (isNewLinking) {
          showNotification({
            title: "Success",
            message: "Batch updated successfully. Package student details synced.",
            color: "green",
            icon: <IconCheck size={16} />,
          });
        } else {
          showNotification({
            title: "Success",
            message: "Batch updated successfully. Package student details synced.",
            color: "green",
            icon: <IconCheck size={16} />,
          });
        }

        setBatchModalOpen(false);
        setEditMode(false);
        fetchBatches();
      }
    } catch (error) {
      console.error("Error updating batch:", error);
      showNotification({
        title: "Error",
        message: "Failed to update batch.",
        color: "red",
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInLms = async () => {
    if (!selectedBatch) return;

    try {
      setUpdatingLms(true);

      const formattedInstructors = selectedBatch.instructor?.map(
        (instructor) => ({
          name: instructor.name || "",
          email: instructor.email || "",
          mouAmount: Number(instructor.mouAmount) || 0,
          trainerLeaves: Number(instructor.trainerLeaves) || 0,
          dateOfAssign:
            instructor.dateOfAssign || new Date().toISOString().split("T")[0],
          mouHours: instructor.mouHours || "0",
          attendedHours: instructor.attendedHours || "0",
          batchTiming: instructor.batchTiming || "0",
        }),
      );

      const success = await updateBatch(selectedBatch.batch_no, {
        ...selectedBatch,
        instructor: formattedInstructors,
        triggerLms: true,
      });

      if (success) {
        showNotification({
          title: "LMS Update Triggered",
          message: `Batch #${selectedBatch.batch_no} is being synced to the LMS.`,
          color: "green",
          icon: <IconCheck size={16} />,
        });
        fetchBatches();
      }
    } catch (error) {
      console.error("Error triggering LMS update:", error);
      showNotification({
        title: "Error",
        message: "Failed to trigger LMS update.",
        color: "red",
        icon: <IconX size={16} />,
      });
    } finally {
      setUpdatingLms(false);
    }
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatch) return;
    const success = await deleteBatch(selectedBatch.batch_no);
    if (success) {
      setDeleteModalOpen(false);
    }
  };

  const handleBatchStatusChange = async (batchNo: string, status: string) => {
    await updateBatchStatus(batchNo, status);
  };

  const handleMouStatusChange = async (batchNo: string, status: string) => {
    try {
      // Find the current batch data
      const currentBatch = batches.find((batch) => batch.batch_no === batchNo);

      if (!currentBatch) {
        console.error("Batch not found");
        return;
      }

      // Update only the MouStatus while preserving other fields
      const updatedBatchData = {
        ...currentBatch,
        MouStatus: status,
      };

      await updateBatch(batchNo, updatedBatchData);
      await fetchBatches();
    } catch (error) {
      console.error("Error updating MOU status:", error);
    }
  };

  const handleSessionEdit = (batchNo: string, currentValue: number) => {
    setEditingSessionId(batchNo);
    setSessionInputValue(currentValue);
  };

  const handleSessionSave = async (batchNo: string) => {
    try {
      // Find the current batch being edited
      const currentBatch = batches.find((batch) => batch.batch_no === batchNo);

      if (!currentBatch) {
        console.error("Batch not found");
        return;
      }

      // Update the batch with new session count
      const updatedBatch = { ...currentBatch, numSessions: sessionInputValue };
      await updateBatch(batchNo, updatedBatch);

      // Refresh the batches data
      await fetchBatches();

      // Clear editing state
      setEditingSessionId(null);
    } catch (error) {
      console.error("Error updating session count:", error);
    }
  };

  const handleSessionCancel = () => {
    setEditingSessionId(null);
    setSessionInputValue(0);
  };

  const handleStudentFormChange = (field: string, value: string) => {
    setStudentData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStudentEmailChange = (
    index: number,
    email: string | null,
    isEditMode = false,
  ) => {
    // Find the selected student from the dropdown
    const selectedStudent = studentsRef.current.find((s) => s.email === email);

    if (isEditMode && selectedBatch) {
      // Update Students in Edit Batch Mode
      setSelectedBatch((prev) => {
        if (!prev) return prev;
        const updatedStudents = [...prev.students];
        updatedStudents[index] = {
          ...updatedStudents[index],
          email: email || "",
          name: selectedStudent?.name || "",
        };
        return { ...prev, students: updatedStudents };
      });
    } else {
      // Update Students in Create Batch Mode
      setFormData((prev) => {
        const updatedStudents = [...(prev.students || [])];
        updatedStudents[index] = {
          ...updatedStudents[index],
          email: email || "",
          name: selectedStudent?.name || "",
        };
        return { ...prev, students: updatedStudents };
      });
    }
  };

  const handleStudentChange = (
    index: number,
    field: keyof Student,
    value: any,
    isEdit: boolean = false,
  ) => {
    if (isEdit && selectedBatch) {
      setSelectedBatch((prev) => {
        if (!prev) return prev;
        const updatedStudents = [...prev.students];
        const updatedStudent = {
          ...updatedStudents[index],
          [field]: value,
        };
        const amount = Number(updatedStudent.amount) || 0;
        const received = Number(updatedStudent.received) || 0;
        updatedStudent.balance = Math.max(amount - received, 0);
        updatedStudents[index] = updatedStudent;
        return { ...prev, students: updatedStudents };
      });
    } else {
      setFormData((prev) => {
        const updatedStudents = [...(prev.students || [])];
        const updatedStudent = {
          ...updatedStudents[index],
          [field]: value,
        };
        const amount = Number(updatedStudent.amount) || 0;
        const received = Number(updatedStudent.received) || 0;
        updatedStudent.balance = Math.max(amount - received, 0);
        updatedStudents[index] = updatedStudent;
        return { ...prev, students: updatedStudents };
      });
    }
  };

  const handleCreateStudent = async () => {
    if (
      !studentData.name ||
      !studentData.email ||
      !studentData.mobile ||
      !studentData.enrollDate
    ) {
      alert("Please fill all required fields!");
      return;
    }

    if (!validateStudentEmail(studentData.email)) {
      alert("This email already exists!");
      return;
    }

    const success = await createStudent(studentData);
    if (success) {
      setStudentModalOpen(false);
      resetStudentData();
    }
  };

  const handleOpenStudentModal = async () => {
    const nextId = await fetchNextStudentId();
    setStudentData((prev) => ({ ...prev, student_id: nextId }));
    setStudentModalOpen(true);
  };

  // Utility Functions
  const resetFormData = () => {
    setFormData({
      batch_no: "",
      courseName: "",
      trainerName: "",
      trainerEmail: "",
      admin: "",
      MouStatus: "",
      batchStatus: "On-going",
       meetingLink: '',
    meetingId: '',
      students: [],
      instructor: [],
    });
  };

  const resetStudentData = () => {
    setStudentData({
      student_id: "",
      name: "",
      email: "",
      mobile: "",
      enrollDate: "",
    });
  };

  const handleResetFilters = () => {
    setCombinedFilter("");
    setTrainerNameFilter("");
    setStatusFilter("");
    setFilterAdmin("");
    setScheduleStatusFilter([]);
    setStudentNameFilter("");
    setOrientationFilter("");
    setBridgeCourseFilter("");
    setCurrentPage(1);
    setRowsPerPage(15);
    sessionStorage.removeItem("batchFilters");
  };

  // Short export: just the Orientation / Bridge Course status per batch
  const exportOrientationBridgeCSV = () => {
    if (filteredBatch.length === 0) {
      alert("No data available for export.");
      return;
    }

    const headers = [
      "Batch Number",
      "Course Name",
      "Trainer Name",
      "Orientation",
      "Bridge Course",
    ];

    const csvRows = filteredBatch.map((batch) => [
      batch.batch_no || "",
      batch.courseName || "",
      batch.trainerName || "",
      batch.orientation || "Not Set",
      batch.bridgeCourse || "Not Set",
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(
      blob,
      `batches_orientation_bridge_${new Date().toISOString().split("T")[0]}.csv`,
    );
  };

  const exportToCSV = () => {
    if (filteredBatch.length === 0) {
      alert("No data available for export.");
      return;
    }

    // Helper function to parse DynamoDB format
    const parseDynamoDBValue = (value: any) => {
      if (!value) return null;
      if (value.S) return value.S; // String
      if (value.N) return value.N; // Number
      if (value.M) return value.M; // Map
      return value;
    };

    // Helper function to parse DynamoDB array of maps
    const parseDynamoDBArray = (array: any[]): any[] => {
      if (!Array.isArray(array)) return [];
      return array.map((item: any) => {
        if (item.M) {
          const parsed: { [key: string]: any } = {};
          Object.keys(item.M).forEach((key: string) => {
            parsed[key] = parseDynamoDBValue(item.M[key]);
          });
          return parsed;
        }
        return item;
      });
    };
    // Headers for CSV
    const headers = [
      "Batch Number",
      "Course Name",
      "Trainer Name",
      "Old Trainer Name1",
      "Old Trainer Name2",
      "MOU Status",
      "Admin",
      "Number of Sessions",
      "Batch Status",
      "Start Date",
      "Schedule Status",
      "Schedule Details",
      "Orientation",
      "Bridge Course",
      "Comments",
      // Students
      "Student Emails",
      "Student Names",
      "Student Amounts",
      "Currency",
      "Received",
      "Balance",
      "Referral",
      "Payment Status",
      "Student Comments",
      "Student Classes",
      // Instructors
      "Instructor Names",
      "Instructor Emails",
      "Date Of Assign",
      "MOU Hours",
      "MOU Amount",
      "Trainer Leaves",
      "Attended Hours",
      "Batch Timings",
    ];

    const csvRows = filteredBatch.map((batch) => {
      // Parse students data from DynamoDB format
      const studentsData = parseDynamoDBArray(batch.students || []);

      const studentEmails =
        studentsData.map((s) => s.email || "N/A").join(" | ") || "N/A";
      const studentNames =
        studentsData.map((s) => s.name || "N/A").join(" | ") || "N/A";
      const studentAmounts =
        studentsData.map((s) => s.amount || "N/A").join(" | ") || "N/A";
      const studentCurrency =
        studentsData.map((s) => s.currency || "N/A").join(" | ") || "N/A";
      const studentReceived =
        studentsData.map((s) => s.received || "N/A").join(" | ") || "N/A";
      const studentBalance =
        studentsData.map((s) => s.balance || "N/A").join(" | ") || "N/A";
      const studentReferral =
        studentsData.map((s) => s.referral || "N/A").join(" | ") || "N/A";
      const studentPaymentStatus =
        studentsData.map((s) => s.paymentStatus || "N/A").join(" | ") || "N/A";
      const studentComments =
        studentsData.map((s) => s.comment || "N/A").join(" | ") || "N/A";
      const studentClasses =
        studentsData.map((s) => s.studentClasses || "N/A").join(" | ") || "N/A";

      // Parse instructors data from DynamoDB format
      const instructorsData = parseDynamoDBArray(batch.instructor || []);

      const instructorNames =
        instructorsData.map((i) => i.name || "N/A").join(" | ") || "N/A";
      const instructorEmails =
        instructorsData.map((i) => i.email || "N/A").join(" | ") || "N/A";
      const instructorDates =
        instructorsData.map((i) => i.dateOfAssign || "N/A").join(" | ") ||
        "N/A";
      const instructorMouHours =
        instructorsData.map((i) => i.mouHours || "N/A").join(" | ") || "N/A";
      const instructorMouAmount =
        instructorsData.map((i) => i.mouAmount || "N/A").join(" | ") || "N/A";
      const instructorLeaves =
        instructorsData.map((i) => i.trainerLeaves || "N/A").join(" | ") ||
        "N/A";
      const instructorAttended =
        instructorsData.map((i) => i.attendedHours || "N/A").join(" | ") ||
        "N/A";
      const instructorTimings =
        instructorsData.map((i) => i.batchTiming || "N/A").join(" | ") || "N/A";

      return [
        batch.batch_no || "",
        batch.courseName || "",
        batch.trainerName || "",
        batch.oldTrainerName1 || "",
        batch.oldTrainerName2 || "",
        batch.MouStatus || "",
        batch.admin || "",
        batch.numSessions || "",
        batch.batchStatus || "",
        batch.startDate || "",
        batch.scheduleStatus || "",
        batch.scheduleDetails || "",
        batch.orientation || "",
        batch.bridgeCourse || "",
        batch.comments || "",
        // Students
        studentEmails,
        studentNames,
        studentAmounts,
        studentCurrency,
        studentReceived,
        studentBalance,
        studentReferral,
        studentPaymentStatus,
        studentComments,
        studentClasses,
        // Instructors
        instructorNames,
        instructorEmails,
        instructorDates,
        instructorMouHours,
        instructorMouAmount,
        instructorLeaves,
        instructorAttended,
        instructorTimings,
      ];
    });

    // Build CSV content
    const csvContent = [headers, ...csvRows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `batches_${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleAddInstructor = (isEdit: boolean = false) => {
    const newInstructor = {
      name: "",
      email: "",
      dateOfAssign: new Date().toISOString().split("T")[0],
      mouHours: "0",
      mouAmount: 0,
      trainerLeaves: 0,
      attendedHours: "0",
      batchTiming: "0",
    };

    if (isEdit && selectedBatch) {
      setSelectedBatch((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          instructor: [...(prev.instructor || []), newInstructor],
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        instructor: [...(prev.instructor || []), newInstructor],
      }));
    }
  };

  const handleRemoveInstructor = (index: number, isEdit: boolean = false) => {
    if (isEdit && selectedBatch) {
      const currentInstructors = selectedBatch.instructor || [];
      if (currentInstructors.length <= 1) {
        alert("At least one instructor is required!");
        return;
      }
      setSelectedBatch((prev) => {
        if (!prev) return prev;
        const updatedInstructors = [...(prev.instructor || [])];
        updatedInstructors.splice(index, 1);
        return { ...prev, instructor: updatedInstructors };
      });
    } else {
      const currentInstructors = formData.instructor || [];
      if (currentInstructors.length <= 1) {
        alert("At least one instructor is required!");
        return;
      }
      const updatedInstructors = [...currentInstructors];
      updatedInstructors.splice(index, 1);
      setFormData((prev) => ({
        ...prev,
        instructor: updatedInstructors,
      }));
    }
  };

  const handleSubmitStudent = async () => {
    if (
      !studentData.name ||
      !studentData.email ||
      !studentData.mobile ||
      !studentData.enrollDate ||
      !studentData.student_id
    ) {
      showNotification({
        title: "Missing Fields",
        message: "Please fill all required fields!",
        color: "red",
        icon: <IconX />,
      });
      return false;
    }

    const normalizedEmail = studentData.email.trim().toLowerCase();

    // Check for duplicate email
    const emailExists = studentsRef.current.some(
      (s: Student) => (s.email?.trim().toLowerCase() ?? "") === normalizedEmail,
    );

    if (emailExists) {
      showNotification({
        title: "Duplicate Email",
        message: "This email already exists!",
        color: "red",
        icon: <IconX />,
      });
      return false;
    }

    try {
      setStudentLoading(true);
      const response = await ApiService.getInstance().post(
        "/students",
        studentData,
      );

      showNotification({
        title: "Student Created",
        message: `Student ${studentData.name} added successfully!`,
        color: "green",
      });

      studentsRef.current.push({
        ...studentData,
        currency: "",
        paymentStatus: "",
        comment: "",
        studentClasses: "",
      });

      // Update dropdown with all required props
      const newStudentOption = {
        label: studentData.email,
        value: studentData.email,
        name: studentData.name,
      };

      setStudentsDropdown(
        (prev: { label: string; value: string; name: string }[]) => [
          ...prev,
          newStudentOption,
        ],
      );

      // Reset and close modal
      setStudentModalOpen(false);
      setStudentData({
        student_id: "",
        name: "",
        email: "",
        mobile: "",
        enrollDate: "",
      });

      // Refresh students list
      fetchStudents();
      return true;
    } catch (error) {
      console.error("Error creating student:", error);
      showNotification({
        title: "Error",
        message: "Failed to create student",
        color: "red",
        icon: <IconX />,
      });
      return false;
    } finally {
      setStudentLoading(false);
    }
  };

  // Optional: Helper to get merge preview from backend
  async function getMergePreview(batchNos: string[]) {
    try {
      const response = await ApiService.getInstance().post(
        "/batch/merge-preview",
        { batchNos },
      );
      return response.data;
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to fetch merge preview.",
        color: "red",
        icon: <IconX size={16} />,
      });
      return null;
    }
  }

  // Optional: Add a separate merge preview function
  // const handleMergePreview = async (batchNos: string[]) => {
  //   if (batchNos.length < 2) return;

  //   const preview = await getMergePreview(batchNos);
  //   if (preview) {
  //     const message = `
  //       This will merge ${preview.totalBatches} batches:
  //       • ${preview.totalStudents} unique students
  //       • ${preview.totalInstructors} instructors (${preview.duplicateInstructors} will be merged)
  //       ${preview.mergedInstructors?.length > 0 ?
  //         `• Instructors to be merged: ${preview.mergedInstructors.map((i: any) => i.name).join(', ')}`
  //         : ''
  //       }
  //     `;

  //     showNotification({
  //       title: "Merge Preview",
  //       message: message,
  //       color: "blue",
  //       autoClose: 8000,
  //     });
  //   }
  // };

  // Add this function after handleMergePreview
  const handleUnmergeBatches = async (batchNos: string[]) => {
    try {
      const success = await unlinkBatches(batchNos);
      if (success) {
        // Close modal and refresh data
        setBatchModalOpen(false);
        setEditMode(false);
        setSelectedBatch(null);
        await fetchBatches();

        showNotification({
          title: "Success",
          message: "Batches unmerged successfully and original data restored.",
          color: "green",
          icon: <IconCheck size={16} />,
        });
      }
    } catch (error) {
      console.error("Error unmerging batches:", error);
      showNotification({
        title: "Error",
        message: "Failed to unmerge batches.",
        color: "red",
        icon: <IconX size={16} />,
      });
    }
  };

  // Helper function to get batch by batch number
  const getBatchByBatchNo = useCallback(
    (batchNo: string) => {
      return batches.find((batch) => batch.batch_no === batchNo) || null;
    },
    [batches],
  );

  // Merge preview function (if you want this feature)
  const handleMergePreview = async (batchNos: string[]) => {
    if (batchNos.length < 2) return;

    try {
      // Simple preview based on current data
      const selectedBatches = batches.filter((batch) =>
        batchNos.includes(batch.batch_no),
      );

      let totalStudents = 0;
      let totalInstructors = 0;
      const allEmails = new Set();

      selectedBatches.forEach((batch) => {
        if (batch.students) {
          batch.students.forEach((student) => {
            if (student.email && !allEmails.has(student.email)) {
              allEmails.add(student.email);
              totalStudents++;
            }
          });
        }
        if (batch.instructor) {
          totalInstructors += batch.instructor.length;
        }
      });

      showNotification({
        title: "Merge Preview",
        message: `This will merge ${batchNos.length} batches with ${totalStudents} unique students and ${totalInstructors} instructors.`,
        color: "blue",
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Error generating merge preview:", error);
    }
  };
  return (
    // Use a slightly smaller container and reduced vertical padding for a compact UI
    <Container size={1600} className="py-4" style={{ fontSize: 13 }}>
      <Paper
        shadow="md"
        radius="md"
        withBorder
        className="border-gray-200 bg-white"
      >
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <Group justify="apart" align="center">
            <div>
              {/* <Title order={3} className="text-gray-800 text-lg font-semibold mb-0">Batch </Title> */}
              {/* <Text size="xs" className="text-gray-600">Manage and organize your training batches</Text> */}
            </div>
            <Group gap="xs">
              <Tooltip label="Create New Batch" withArrow>
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  variant="gradient"
                  gradient={{ from: "blue", to: "cyan", deg: 90 }}
                  size="xs"
                  className="shadow-md hover:shadow-lg transition-all"
                  leftSection={<FaPlus size={12} />}
                >
                  <span className="text-xs font-semibold">Create Batch</span>
                </Button>
              </Tooltip>
              {isSuperAdmin && !batchOnly && (
                <Tooltip label="View Journey Issues Dashboard" withArrow>
                  <Button
                    onClick={() => navigate("/journey-issues-dashboard")}
                    variant="gradient"
                    gradient={{ from: "red", to: "orange", deg: 90 }}
                    size="xs"
                    className="shadow-md hover:shadow-lg transition-all"
                    leftSection={<IconChalkboard size={14} />}
                  >
                    <span className="text-xs font-semibold">
                      Journey Dashboard
                    </span>
                  </Button>
                </Tooltip>
              )}
              {isSuperAdmin && !batchOnly && (
                <Tooltip label="View Recent Batch Changes" withArrow>
                  <Button
                    onClick={() => navigate("/recent-changes")}
                    variant="gradient"
                    gradient={{ from: "violet", to: "grape", deg: 90 }}
                    size="xs"
                    className="shadow-md hover:shadow-lg transition-all"
                    leftSection={<IconChalkboard size={14} />}
                  >
                    <span className="text-xs font-semibold">
                      Recent Changes
                    </span>
                  </Button>
                </Tooltip>
              )}
              <Tooltip label="Show Filters" withArrow>
                <ActionIcon
                  variant="light"
                  color="blue"
                  onClick={() => setShowFilters(!showFilters)}
                  size="lg"
                  className="hover:bg-blue-100 transition-colors"
                >
                  <FaFilter size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Reset Filters" withArrow>
                <ActionIcon
                  variant="light"
                  color="gray"
                  onClick={handleResetFilters}
                  size="lg"
                  className="hover:bg-gray-100 transition-colors"
                >
                  <FaRedo size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Refresh Batch List" withArrow>
                <ActionIcon
                  variant="light"
                  color="green"
                  onClick={fetchBatches}
                  size="lg"
                  className="hover:bg-green-100 transition-colors"
                >
                  <FaSync size={14} />
                </ActionIcon>
              </Tooltip>
              <Menu shadow="md" width={260} position="bottom-start" withArrow>
                <Menu.Target>
                  <Tooltip label="Export CSV" withArrow>
                    <ActionIcon
                      variant="light"
                      color="teal"
                      size="lg"
                      className="hover:bg-teal-100 transition-colors"
                    >
                      <FaFileCsv size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    Exports {filteredBatch.length} filtered batch(es)
                  </Menu.Label>
                  <Menu.Item onClick={exportToCSV}>Full Batch CSV</Menu.Item>
                  <Menu.Item onClick={exportOrientationBridgeCSV}>
                    Orientation &amp; Bridge Course CSV
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
  {/* ── Student Rating button (COMMENTED OUT) ──
  <Tooltip label="Student Rating Filter" withArrow>
  <Button
    variant="filled"
    onClick={() => setSweetnessFilterOpen(true)}
    styles={{
      root: {
        backgroundColor: "#221E45",
        color: "#FFFFFF",
      },
    }}
    className="hover:opacity-90 transition-opacity"
  >
    Student Rating

  </Button>
</Tooltip>
  */}
            </Group>
          </Group>
        </div>

        <BatchFilters
          showFilters={showFilters}
          combinedFilter={combinedFilter}
          trainerNameFilter={trainerNameFilter}
          statusFilter={statusFilter}
          scheduleStatusFilter={scheduleStatusFilter}
          filterAdmin={filterAdmin}
          studentNameFilter={studentNameFilter}
          orientationFilter={orientationFilter}
          bridgeCourseFilter={bridgeCourseFilter}
          onCombinedFilterChange={setCombinedFilter}
          onTrainerNameFilterChange={setTrainerNameFilter}
          onStatusFilterChange={setStatusFilter}
          onScheduleStatusFilterChange={setScheduleStatusFilter}
          onFilterAdminChange={setFilterAdmin}
          onStudentNameFilterChange={setStudentNameFilter}
          onOrientationFilterChange={setOrientationFilter}
          onBridgeCourseFilterChange={setBridgeCourseFilter}
          onResetFilters={handleResetFilters}
          adminDropdown={adminDropdown}
        />

        {/* Pagination and Stats Section */}
        <div className="bg-gray-50 border-t border-b border-gray-200 px-3 md:px-6 py-3 mt-4">
          {isMobile ? (
            <Stack gap="sm">
              <Flex justify="center" align="center">
                <Badge
                  variant="light"
                  color="blue"
                  size="sm"
                  className="font-semibold"
                >
                  Total: {filteredBatch.length}
                </Badge>
              </Flex>
              <Flex justify="center" align="center">
                <Pagination
                  total={Math.ceil(filteredBatch.length / rowsPerPage)}
                  value={currentPage}
                  onChange={setCurrentPage}
                  size="xs"
                  radius="md"
                  withEdges={false}
                  siblings={0}
                  boundaries={1}
                />
              </Flex>
              <Flex justify="center" align="center">
                <Select
                  label={
                    <Text size="xs" className="text-gray-600 font-medium">
                      Rows
                    </Text>
                  }
                  value={rowsPerPage.toString()}
                  onChange={(value) => {
                    setRowsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                  data={PAGE_OPTIONS.map(String)}
                  size="xs"
                  className="w-24"
                />
              </Flex>
            </Stack>
          ) : (
            <Flex justify="space-between" align="center" gap="md">
              <Pagination
                total={Math.ceil(filteredBatch.length / rowsPerPage)}
                value={currentPage}
                onChange={setCurrentPage}
                size="sm"
                radius="md"
              />
              <div className="flex items-center gap-2">
                <Badge
                  variant="light"
                  color="blue"
                  size="md"
                  className="font-semibold"
                >
                  Total Records: {filteredBatch.length}
                </Badge>
              </div>
              <Select
                label={
                  <Text size="xs" className="text-gray-600 font-medium">
                    Rows per page
                  </Text>
                }
                value={rowsPerPage.toString()}
                onChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(1);
                }}
                data={PAGE_OPTIONS.map(String)}
                size="sm"
                className="w-32"
              />
            </Flex>
          )}
        </div>

        {/* Table Section */}
        <div className="px-6 py-4">
          {loading ? (
            <div className="text-center py-12">
              <LoadingOverlay visible={true} />
              <Text size="sm" className="text-gray-500 mt-4">
                Loading batches...
              </Text>
            </div>
          ) : (
            <BatchTable
              batches={paginatedBatch}
              onBatchClick={handleBatchClick}
              onDeleteClick={(batch) => {
                setSelectedBatch(batch);
                setDeleteModalOpen(true);
              }}
              onStatusChange={handleBatchStatusChange}
              onMouStatusChange={handleMouStatusChange}
              onSessionEdit={handleSessionEdit}
              editingSessionId={editingSessionId}
              sessionInputValue={sessionInputValue}
              onSessionSave={handleSessionSave}
              onSessionCancel={handleSessionCancel}
              onSessionValueChange={setSessionInputValue}
              compact={true}
              onUploadResource={(batchNo) => {
                setSelectedBatchForResource(batchNo);
                setOpenUploadModal(true);
              }}
              onViewResource={(batchNo) => {
                setSelectedBatchForResource(batchNo);
                setOpenViewModal(true);
              }}
            />
          )}
        </div>

        {/* Create Batch Modal */}
        <Modal
          opened={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title={
            <Group gap="xs">
              <IconPlus size={18} className="text-blue-600" />
              <Text size="md" fw={600} className="text-gray-800">
                Create New Batch
              </Text>
            </Group>
          }
          size="lg"
          padding="lg"
          radius="md"
          styles={{
            body: { maxHeight: "75vh", overflowY: "auto" },
            header: {
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "0.75rem",
            },
          }}
        >
          <LoadingOverlay visible={loading} />
          <BatchForm
            formData={formData}
            onChange={(field, value) =>
              setFormData((prev) => ({ ...prev, [field]: value }))
            }
            batchNumbers={batches.map((b) => b.batch_no)}
            editMode={true}
            isSuperAdmin={isSuperAdmin}
            handleUnmergeBatches={handleUnmergeBatches}
            getBatchByBatchNo={getBatchByBatchNo}
            handleMergePreview={handleMergePreview}
            adminDropdown={adminDropdown}
            instructorsDropdown={instructors}
            instructorsRef={instructorsRef}
            studentsDropdown={studentsDropdown}
            studentsRef={studentsRef}
            studentData={studentData}
            onStudentChange={handleStudentFormChange}
            onCreateStudent={handleSubmitStudent}
            onOpenCreateStudentModal={handleOpenStudentModal}
            studentLoading={studentLoading}
            onStudentFormChange={(index, field, value) =>
              handleStudentChange(index, field, value, false)
            }
            onStudentEmailChange={(index, email) =>
              handleStudentEmailChange(index, email, false)
            }
            onInstructorFormChange={(index, field, value) =>
              handleInstructorChange(index, field, value, false)
            }
            onInstructorEmailChange={(index, email) =>
              handleInstructorEmailChange(index, email, false)
            }
            onAddStudent={() =>
              setFormData((prev) => ({
                ...prev,
                students: [
                  ...(prev.students || []),
                  {
                    name: "",
                    amount: undefined,
                    received: undefined,
                    balance: undefined,
                    referral: undefined,
                    currency: "",
                    paymentStatus: "",
                    comment: "",
                    studentClasses: "",
                  },
                ],
              }))
            }
            onRemoveStudent={(index) => {
              if (formData.students?.length === 1) {
                alert("At least one student is required!");
                return;
              }
              setFormData((prev) => ({
                ...prev,
                students: prev.students?.filter((_, i) => i !== index),
              }));
            }}
            onAddInstructor={() => handleAddInstructor(false)}
            onRemoveInstructor={(index) => handleRemoveInstructor(index, false)}
            isEditBatch={false}
          />

          {/* Action Button */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200 mt-6 -mx-6 px-6 py-4 rounded-b-md">
            <Group justify="flex-end">
              <Button
                variant="gradient"
                gradient={{ from: "blue", to: "cyan" }}
                size="sm"
                radius="md"
                onClick={handleCreateBatch}
                leftSection={<IconCheck size={16} />}
                className="shadow-md hover:shadow-lg transition-all font-semibold text-xs px-6"
              >
                Create Batch
              </Button>
            </Group>
          </div>

          {/* Create Student Modal - Nested inside Create Batch Modal */}
          <CreateStudent
            opened={studentModalOpen}
            onClose={() => setStudentModalOpen(false)}
            loading={studentLoading}
            studentData={studentData}
            onStudentChange={handleStudentFormChange}
            onSubmit={async () => {
              const success = await handleSubmitStudent();
              if (success) {
                // Student will be available in dropdown after creation
                // No need to add to formData here as user will select from dropdown
              }
            }}
          />
        </Modal>

        {/* LMS Creation Confirmation Modal */}
        <Modal
          opened={lmsConfirmOpen}
          onClose={() => {
            if (creatingBatch) return;
            setLmsConfirmOpen(false);
            setPendingBatchData(null);
          }}
          title={
            <Group gap="xs">
              <IconCloudUpload size={18} className="text-grape-600" />
              <Text size="md" fw={600} className="text-gray-800">
                Create Course on LMS?
              </Text>
            </Group>
          }
          centered
          radius="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Do you want to also create this batch as a course on the LMS?
            </Text>

            {pendingBatchData && (
              <Paper withBorder radius="md" p="sm" className="bg-gray-50">
                <Stack gap={4}>
                  <Text size="xs">
                    <b>Batch No:</b> {pendingBatchData.batch_no || "—"}
                  </Text>
                  <Text size="xs">
                    <b>Course:</b> {pendingBatchData.courseName || "—"}
                  </Text>
                  <Text size="xs">
                    <b>Trainer Email:</b>{" "}
                    {pendingBatchData.trainerEmail || "—"}
                  </Text>
                  <Text size="xs">
                    <b>Meeting Link:</b> {pendingBatchData.meetingLink || "—"}
                  </Text>
                  <Text size="xs">
                    <b>Class Timing:</b>{" "}
                    {pendingBatchData.instructor?.[0]?.batchTiming || "—"}
                  </Text>
                </Stack>
              </Paper>
            )}

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                color="gray"
                radius="md"
                size="sm"
                leftSection={<IconX size={14} />}
                onClick={() => handleConfirmCreateBatch(false)}
                loading={creatingBatch}
                className="font-semibold text-xs"
              >
                No, just create batch
              </Button>
              <Button
                variant="gradient"
                gradient={{ from: "grape", to: "violet" }}
                radius="md"
                size="sm"
                leftSection={<IconCheck size={14} />}
                onClick={() => handleConfirmCreateBatch(true)}
                loading={creatingBatch}
                className="font-semibold text-xs"
              >
                Yes, create on LMS
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={
            <Group gap="xs">
              <IconTrash size={18} className="text-red-600" />
              <Text size="md" fw={600} className="text-gray-800">
                Confirm Deletion
              </Text>
            </Group>
          }
          centered
          radius="md"
          padding="lg"
        >
          <div className="py-2">
            <Text size="sm" className="text-gray-700 mb-4">
              Are you sure you want to delete batch{" "}
              <span className="font-bold text-red-600">
                {selectedBatch?.batch_no}
              </span>
              ?
            </Text>
            <Text size="xs" className="text-gray-500 mb-6">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                color="gray"
                onClick={() => setDeleteModalOpen(false)}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteBatch}
                leftSection={<IconTrash size={16} />}
                size="sm"
                className="shadow-md"
              >
                Delete Batch
              </Button>
            </Group>
          </div>
        </Modal>

        {/* Batch Details Modal */}
        <Modal
          opened={batchModalOpen}
          onClose={() => setBatchModalOpen(false)}
          title={
            <Group gap="xs">
              <IconChalkboard size={18} className="text-indigo-600" />
              <Text size="md" fw={600} className="text-gray-800">
                Batch Details
              </Text>
            </Group>
          }
          size="lg"
          padding="lg"
          radius="md"
          styles={{
            body: { maxHeight: "75vh", overflowY: "auto" },
            header: {
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "0.75rem",
            },
          }}
        >
          <LoadingOverlay visible={loading} />

          {selectedBatch && (
            <Stack gap="md">
              {/* Header Actions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="xs" fw={600} className="text-blue-800">
                      Batch Number:{" "}
                      <span className="text-blue-600">
                        {selectedBatch.batch_no}
                      </span>
                    </Text>
                    <Text size="xs" className="text-gray-600 mt-1">
                      {editMode
                        ? "Edit mode enabled - Make your changes below"
                        : "View mode - Click edit to modify"}
                    </Text>
                  </div>
                  {!editMode && (
                    <Button
                      onClick={() => setEditMode(true)}
                      variant="light"
                      color="blue"
                      leftSection={<IconEdit size={14} />}
                      radius="md"
                      size="xs"
                      className="font-semibold"
                    >
                      Edit Batch
                    </Button>
                  )}
                </Group>
              </div>

              {/* Batch Form Section with Students and Instructors integrated */}
              <Paper p="md" radius="md" withBorder>
                <BatchForm
                  formData={selectedBatch}
                  onChange={(field, value) =>
                    setSelectedBatch((prev) =>
                      prev ? { ...prev, [field]: value } : null,
                    )
                  }
                  editMode={editMode}
                  isSuperAdmin={isSuperAdmin}
                  batchNumbers={batches.map((b) => b.batch_no)}
                  currentBatchNo={selectedBatch.batch_no}
                  handleUnmergeBatches={handleUnmergeBatches}
                  getBatchByBatchNo={getBatchByBatchNo}
                  handleMergePreview={handleMergePreview}
                  adminDropdown={adminDropdown}
                  instructorsDropdown={instructors}
                  instructorsRef={instructorsRef}
                  studentsDropdown={studentsDropdown}
                  studentsRef={studentsRef}
                  studentData={studentData}
                  onStudentChange={handleStudentFormChange}
                  onCreateStudent={handleSubmitStudent}
                  onOpenCreateStudentModal={handleOpenStudentModal}
                  studentLoading={studentLoading}
                  onStudentFormChange={(index, field, value) =>
                    handleStudentChange(index, field, value, true)
                  }
                  onStudentEmailChange={(index, email) =>
                    handleStudentEmailChange(index, email, true)
                  }
                  onInstructorFormChange={(index, field, value) =>
                    handleInstructorChange(index, field, value, true)
                  }
                  onInstructorEmailChange={(index, email) =>
                    handleInstructorEmailChange(index, email, true)
                  }
                  onAddStudent={() =>
                    setSelectedBatch((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        students: [
                          ...prev.students,
                          {
                            name: "",
                            amount: undefined,
                            received: undefined,
                            balance: undefined,
                            referral: undefined,
                            currency: "",
                            paymentStatus: "",
                            comment: "",
                            studentClasses: "",
                          },
                        ],
                      };
                    })
                  }
                  onRemoveStudent={(index) => {
                    if (selectedBatch.students?.length === 1) {
                      alert("At least one student is required!");
                      return;
                    }
                    setSelectedBatch((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        students: prev.students.filter((_, i) => i !== index),
                      };
                    });
                  }}
                  onAddInstructor={() => handleAddInstructor(true)}
                  onRemoveInstructor={(index) =>
                    handleRemoveInstructor(index, true)
                  }
                  isEditBatch={true}
                />
              </Paper>

              {/* Action Buttons - Only show in edit mode */}
              {editMode && (
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200 mt-6 -mx-6 px-6 py-4 rounded-b-md">
                  <Group justify="flex-end" gap="xs">
                    <Button
                      color="gray"
                      variant="subtle"
                      radius="md"
                      size="sm"
                      leftSection={<IconX size={14} />}
                      onClick={() => setEditMode(false)}
                      className="font-semibold text-xs"
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="outline"
                      color="grape"
                      size="sm"
                      radius="md"
                      loading={updatingLms}
                      leftSection={<IconCloudUpload size={14} />}
                      onClick={handleUpdateInLms}
                      className="font-semibold text-xs px-6"
                    >
                      Update in LMS
                    </Button>

                    <Button
                      variant="gradient"
                      gradient={{ from: "blue", to: "cyan" }}
                      size="sm"
                      radius="md"
                      leftSection={<IconCheck size={14} />}
                      onClick={handleUpdateBatch}
                      className="shadow-md hover:shadow-lg transition-all font-semibold text-xs px-6"
                    >
                      Update Batch
                    </Button>
                  </Group>
                </div>
              )}

              {/* Create Student Modal - Nested inside Batch Details Modal */}
              <CreateStudent
                opened={studentModalOpen}
                onClose={() => setStudentModalOpen(false)}
                loading={studentLoading}
                studentData={studentData}
                onStudentChange={handleStudentFormChange}
                onSubmit={async () => {
                  const success = await handleSubmitStudent();
                  if (success) {
                    // If in create mode, student will be available in dropdown
                    // If in edit mode and selectedBatch exists, add to batch
                    if (selectedBatch && editMode) {
                      setSelectedBatch((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          students: [
                            ...prev.students,
                            {
                              ...studentData,
                              amount: undefined,
                              received: undefined,
                              balance: undefined,
                              referral: undefined,
                              currency: "",
                              paymentStatus: "",
                              comment: "",
                              studentClasses: "",
                            },
                          ],
                        };
                      });
                    }
                  }
                }}
              />
            </Stack>
          )}
        </Modal>
      </Paper>
      <UploadResourceModal
        isOpen={openUploadModal}
        batchNo={selectedBatchForResource}
        onClose={() => {
          setOpenUploadModal(false);
          setSelectedBatchForResource(null);
        }}
      />

      <ViewResourcesModal
        isOpen={openViewModal}
        batchNo={selectedBatchForResource}
        onClose={() => {
          setOpenViewModal(false);
          setSelectedBatchForResource(null);
        }}
      />

      {/* ── SweetnessRatingFilterModal (COMMENTED OUT) ──
      <SweetnessRatingFilterModal
        opened={sweetnessFilterOpen}
        onClose={() => setSweetnessFilterOpen(false)}
      />
      */}
    </Container>
  );
};

export default Batches;
